import { encodeAbiParameters } from 'viem'
import { termMarketsByChain } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import { isFailedCall } from '../../../utils/multicall'
import {
  decodeVariableLensRows,
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'

export const usesTermLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.TERM) !== undefined

/**
 * ONE call instead of `3 + collaterals` per repo.
 *
 * Term is the largest single-chain roster we serve — **404 calls on Ethereum under
 * one lender key** — and an account is in a handful of repos at most. The lens
 * probes the repo-token balance and the borrow obligation (a lender holds the
 * token, a borrower carries an obligation) and only pays for `redemptionValue` and
 * the per-collateral balances on repos that answer.
 */
export const buildTermLensCall = (
  chainId: string,
  markets: any[],
  account: string,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.TERM)
  if (!lens) return []
  if (
    markets.some(
      (m) => !m.repoToken || !m.servicer || !m.collateralManager || !m.collateralParams,
    )
  ) {
    // Fail closed to the walk rather than encode a payload with a hole in it —
    // `encodeAbiParameters` throws on `undefined`, which would take the whole
    // request down instead of this one lender.
    return []
  }
  const payload = encodeAbiParameters(
    [
      {
        type: 'tuple[]',
        components: [
          { name: 'repoToken', type: 'address' },
          { name: 'servicer', type: 'address' },
          { name: 'collateralManager', type: 'address' },
          { name: 'collateralTokens', type: 'address[]' },
        ],
      },
    ],
    [
      markets.map((m) => ({
        repoToken: m.repoToken,
        servicer: m.servicer,
        collateralManager: m.collateralManager,
        collateralTokens: m.collateralParams.map((c: any) => c.token),
      })) as any,
    ],
  )
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.TERM, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

/**
 * Rebuild the flat `data` array the per-repo walk would have produced, so every
 * cursor arithmetic in the converter is untouched.
 *
 * A repo the lens did not emit is filled with ZEROS — exactly what the walk
 * returns for an account holding none of it.
 *
 * Rows here are VARIABLE width (`uint16 index, uint8 collateralCount, 3 fixed
 * words, collateralCount words`), because a repo carries one collateral entry per
 * accepted token. `decodeLensRows` cannot express that: it derives a stride from a
 * constant word count and would fold the next row's index into the current row's
 * last collateral balance — a plausible number rather than an error.
 *
 * Returns `undefined` when the blob is unusable, which the caller must treat as an
 * incomplete read and never as an empty position.
 */
export const rebuildTermData = (
  blobs: unknown,
  markets: any[],
): any[] | undefined => {
  if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
    return undefined
  }
  let rows
  try {
    rows = decodeVariableLensRows(blobs[0] as string, 3, 0).rows
  } catch {
    return undefined
  }

  const out: any[] = []
  const offsets: number[] = []
  for (const m of markets) {
    offsets.push(out.length)
    out.push(0n, 0n, 0n)
    for (let c = 0; c < m.collateralParams.length; c++) out.push(0n)
  }
  for (const r of rows) {
    if (r.index >= markets.length) return undefined
    const base = offsets[r.index]
    // balance, redemptionValue, obligation — the walk's own order.
    out[base] = r.fixed[0]
    out[base + 1] = r.fixed[1]
    out[base + 2] = r.fixed[2]
    if (r.tail.length !== markets[r.index].collateralParams.length) return undefined
    for (let c = 0; c < r.tail.length; c++) out[base + 3 + c] = r.tail[c]
  }
  return out
}

/** Markets in the order both paths walk them. */
export const termMarketsFor = (chainId: string) => termMarketsByChain(chainId) as any[]
